import { useState } from 'react';
import { BookmarkPlus, Star, Trash2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  useSavedAnalyticsViews,
  type SavedAnalyticsView,
  type SavedViewConfig,
} from '@/hooks/useSavedAnalyticsViews';

interface SavedViewsMenuProps {
  workspaceId: string;
  userId: string;
  isAdmin: boolean;
  currentConfig: SavedViewConfig;
  onLoad: (config: SavedViewConfig) => void;
}

export function SavedViewsMenu({
  workspaceId,
  userId,
  isAdmin,
  currentConfig,
  onLoad,
}: SavedViewsMenuProps) {
  const { toast } = useToast();
  const { views, isLoading, createView, deleteView, setWorkspaceDefault } =
    useSavedAnalyticsViews(workspaceId);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);

  const personalViews = views.filter((v) => v.owner_user_id === userId);
  const sharedViews = views.filter(
    (v) => v.owner_user_id !== userId && (v.shared || v.is_workspace_default),
  );

  function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    createView.mutate(
      { ownerId: userId, name: trimmed, config: currentConfig, shared },
      {
        onSuccess: () => {
          toast({ description: `Saved view "${trimmed}"` });
          setSaveOpen(false);
          setName('');
          setShared(false);
        },
        onError: (err) => {
          toast({
            title: 'Error',
            description: (err as Error)?.message ?? 'Failed to save view',
            variant: 'destructive',
          });
        },
      },
    );
  }

  function handleDelete(view: SavedAnalyticsView, ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    deleteView.mutate(view.id, {
      onSuccess: () => toast({ description: `Deleted "${view.name}"` }),
      onError: (err) =>
        toast({
          title: 'Error',
          description: (err as Error)?.message ?? 'Failed to delete view',
          variant: 'destructive',
        }),
    });
  }

  function handleSetDefault(view: SavedAnalyticsView, ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    setWorkspaceDefault.mutate(view.id, {
      onSuccess: () =>
        toast({ description: `"${view.name}" set as workspace default` }),
      onError: (err) =>
        toast({
          title: 'Error',
          description: (err as Error)?.message ?? 'Failed to set default',
          variant: 'destructive',
        }),
    });
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            <BookmarkPlus className="h-3.5 w-3.5 mr-1" />
            Saved
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuItem
            onSelect={() => {
              setSaveOpen(true);
              setName('');
              setShared(false);
            }}
          >
            <BookmarkPlus className="h-4 w-4 mr-2" />
            Save current view…
          </DropdownMenuItem>

          {(personalViews.length > 0 || sharedViews.length > 0 || isLoading) && (
            <DropdownMenuSeparator />
          )}

          {isLoading && (
            <div className="px-2 py-1.5">
              <Skeleton className="h-4 w-full" />
            </div>
          )}

          {personalViews.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                My views
              </DropdownMenuLabel>
              {personalViews.map((view) => (
                <ViewRow
                  key={view.id}
                  view={view}
                  isOwner
                  isAdmin={isAdmin}
                  onLoad={() => onLoad(view.config)}
                  onDelete={(ev) => handleDelete(view, ev)}
                  onSetDefault={(ev) => handleSetDefault(view, ev)}
                />
              ))}
            </>
          )}

          {sharedViews.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Shared
              </DropdownMenuLabel>
              {sharedViews.map((view) => (
                <ViewRow
                  key={view.id}
                  view={view}
                  isOwner={false}
                  isAdmin={isAdmin}
                  onLoad={() => onLoad(view.config)}
                  onDelete={(ev) => handleDelete(view, ev)}
                  onSetDefault={(ev) => handleSetDefault(view, ev)}
                />
              ))}
            </>
          )}

          {!isLoading && personalViews.length === 0 && sharedViews.length === 0 && (
            <p className="px-2 py-2 text-xs text-muted-foreground">No saved views yet.</p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
            <DialogDescription>
              Save the current chart configuration so you can come back to it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="view-name">Name</Label>
              <Input
                id="view-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Calls vs Meaningful"
                maxLength={100}
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="view-shared"
                checked={shared}
                onCheckedChange={(v) => setShared(v === true)}
              />
              <Label htmlFor="view-shared" className="cursor-pointer">
                Share with workspace
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!name.trim() || createView.isPending}>
              {createView.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ViewRow({
  view,
  isOwner,
  isAdmin,
  onLoad,
  onDelete,
  onSetDefault,
}: {
  view: SavedAnalyticsView;
  isOwner: boolean;
  isAdmin: boolean;
  onLoad: () => void;
  onDelete: (ev: React.MouseEvent) => void;
  onSetDefault: (ev: React.MouseEvent) => void;
}) {
  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        onLoad();
      }}
      className="flex items-center justify-between gap-2 cursor-pointer"
    >
      <span className="flex-1 truncate text-sm">
        {view.name}
        {view.is_workspace_default && (
          <span className="ml-2 text-xs text-muted-foreground">(default)</span>
        )}
      </span>
      <div className="flex items-center gap-0.5 shrink-0">
        {isAdmin && !view.is_workspace_default && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onSetDefault}
            aria-label="Set as workspace default"
          >
            <Star className="h-3.5 w-3.5" />
          </Button>
        )}
        {isOwner && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onDelete}
            aria-label="Delete view"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </DropdownMenuItem>
  );
}
